import AutoScroll from "embla-carousel-auto-scroll";
import useEmblaCarousel from "embla-carousel-react";
import {Children, useEffect, useMemo, useRef} from "react";
import styles from "./carousel.styles.module.scss";
import {CarouselProps} from "./carousel.types";

const DOCUMENT_SCROLL_RESUME_MS = 100;

export const Carousel = ({
  options,
  renderItems,
  children,
  speed = 1,
  pauseOnHover = true,
  pauseOnDocumentScroll = true,
  loop = true,
  autoScroll: autoScrollEnabled = true,
  onInit,
}) => {
  const emblaOptions = useMemo(
    () => ({
      loop: true,
      dragFree: true,
      ...options,
    }),
    [options]
  );

  const plugins = useMemo(() => {
    if (!autoScrollEnabled) return [];
    return [
      AutoScroll({
        playOnInit: true,
        stopOnMouseEnter: pauseOnHover,
        speed,
        stopOnInteraction: false,
        loop,
      }),
    ];
  }, [autoScrollEnabled, speed, pauseOnHover, loop]);

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions, plugins);
  const lastInitEmblaRef = useRef(null);

  const items = useMemo(() => {
    if (typeof renderItems === "function") {
      return renderItems();
    }
    if (renderItems) {
      return renderItems;
    }
    return children;
  }, [renderItems, children]);

  const itemCount = Children.toArray(items).length;

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit();
  }, [emblaApi, itemCount]);

  useEffect(() => {
    if (!emblaApi) {
      lastInitEmblaRef.current = null;
      return;
    }
    if (!onInit) return;
    if (lastInitEmblaRef.current === emblaApi) return;
    lastInitEmblaRef.current = emblaApi;

    const scrollWithPause = scrollFn => {
      const autoScroll = emblaApi.plugins()?.autoScroll;
      if (autoScroll) autoScroll.stop();
      scrollFn();
      if (autoScroll) setTimeout(() => autoScroll.play(), 2000);
    };

    onInit({
      scrollPrev: () => scrollWithPause(() => emblaApi.scrollPrev()),
      scrollNext: () => scrollWithPause(() => emblaApi.scrollNext()),
    });
  }, [emblaApi, onInit]);

  useEffect(() => {
    if (!emblaApi || !pauseOnDocumentScroll) return;
    const autoScroll = emblaApi.plugins()?.autoScroll;
    if (!autoScroll) return;

    let resumeAfterScrollTimer = 0;
    let pausedForDocumentScroll = false;

    const onWindowScroll = () => {
      if (!pausedForDocumentScroll) {
        autoScroll.stop();
        pausedForDocumentScroll = true;
      }
      window.clearTimeout(resumeAfterScrollTimer);
      resumeAfterScrollTimer = window.setTimeout(() => {
        autoScroll.play();
        pausedForDocumentScroll = false;
      }, DOCUMENT_SCROLL_RESUME_MS);
    };

    window.addEventListener("scroll", onWindowScroll, {passive: true});
    return () => {
      window.removeEventListener("scroll", onWindowScroll);
      window.clearTimeout(resumeAfterScrollTimer);
    };
  }, [emblaApi, pauseOnDocumentScroll, autoScrollEnabled]);

  return (
    <div className={styles.carouselWrapper}>
      <div className={styles.leftShadow} />
      <div className={styles.rightShadow} />
      <div className={styles.embla}>
        <div
          className={styles.emblaViewport}
          ref={emblaRef}
        >
          <div className={styles.emblaContainer}>
            {Children.toArray(items).map((child, idx) => (
              <div
                key={child.key || `carousel-slide-${idx}`}
                className={styles.emblaSlide}
              >
                {child}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

Carousel.propTypes = CarouselProps;
